import classNames from 'classnames';
import { useRouter } from 'next/router';
import type { ReactElement, Ref, RefObject, SetStateAction } from 'react';
import { useRef, useState, useEffect } from 'react';
import type { LocationSearchBarProps, LocationSearchBarRef } from './LocationSearchBar.data';
import styles from './LocationSearchBar.module.scss';
import Text from '../../atoms/text/Text';
import Heading from '../../atoms/heading/Heading';
import { useGlobalStore } from '../../../stores/global';
import type { LocationBlokData } from '../../blocks/location/Location.data';
import Link from 'next/link';
import Icon from '../../atoms/icon/Icon';
import useEventListener from '../../../hooks/useEventListener';
import gsap from 'gsap';
import { filterLocations } from '../../../util/locations.utils';

export default function LocationSearchBar({
  className,
  title,
  search_placeholder,
  quick_links_label,
  quick_links,
  parentSetSearchValue,
  ref,
}: LocationSearchBarProps & { ref?: Ref<LocationSearchBarRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const timelineRef = useRef(gsap.timeline());

  const router = useRouter();

  const [indexFocused, setIndexFocused] = useState(-1);
  const [searchValue, _setSearchValue] = useState('');

  const setSearchValue = (value: SetStateAction<string>) => {
    _setSearchValue(value);
    parentSetSearchValue?.(value);
  };

  const { locationsList } = useGlobalStore();

  let results: Array<LocationBlokData> = [];
  if (searchValue) {
    results =
      locationsList
        ?.filter((location) => filterLocations(location, searchValue))
        .sort((a, b) => a.city.localeCompare(b.city)) || [];
  }

  const onKeydown = ({ key }: KeyboardEvent): void => {
    const resultsLength = results.length || quick_links.length;
    if (key === 'ArrowDown') {
      setIndexFocused((i) => (i + 1) % resultsLength);
    }
    if (key === 'ArrowUp') {
      setIndexFocused((i) => {
        if (i === -1) {
          return resultsLength - 1;
        }
        return i - 1;
      });
    }
    if (key === 'Enter' && indexFocused > -1) {
      if (searchValue) {
        router.push(results[indexFocused].office_link.cached_url);
      } else {
        router.push(quick_links[indexFocused].full_slug);
      }
    }
    if (key === 'Escape') {
      setIndexFocused(-1);
      setTimeout(() => inputRef.current?.blur(), 0);
    }
  };

  useEventListener('keydown', onKeydown);

  useEffect(() => {
    if (document.activeElement === inputRef.current) {
      timelineRef.current.to(resultsContainerRef.current, {
        maxHeight: (resultsRef.current?.scrollHeight || 0) + 80,
        paddingBottom: results.length > 0 || !searchValue ? 32 : 0,
      });
    }

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      timelineRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  return (
    <div ref={elementRef} className={classNames(styles.locationSearchBar, className)}>
      <Text
        transitionInType="splitCharsSlideInUp"
        className={classNames(styles.label)}
        as="p"
        size="label-large"
        disableTriggerInOnScroll={false}
      >
        {title}
      </Text>

      <div className={styles.searchContainer}>
        <div className={styles.searchBoxContainer}>
          <input
            ref={inputRef}
            type="text"
            className={styles.searchBox}
            placeholder={search_placeholder}
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            onFocus={() => {
              timelineRef.current.to(resultsContainerRef.current, {
                maxHeight: (resultsRef.current?.scrollHeight || 0) + 80,
                paddingBottom: results.length > 0 || !searchValue ? 32 : 0,
                overwrite: true,
              });
            }}
            onBlur={() => {
              setTimeout(() => {
                if (indexFocused < 0) {
                  timelineRef.current.to(resultsContainerRef.current, {
                    maxHeight: 0,
                    paddingBottom: 0,
                    overwrite: true,
                  });
                }
              }, 0);
            }}
          />
          <Icon name="search" className={classNames(styles.searchIcon)} />

          <Icon
            name="close"
            className={classNames(styles.crossIcon)}
            onClick={() => setSearchValue('')}
            role="button"
          />
        </div>

        <div
          ref={resultsContainerRef}
          className={classNames(styles.resultsContainer, {
            [styles.empty]: searchValue && results.length === 0,
          })}
        >
          {searchValue ? (
            <ul className={styles.results} ref={resultsRef}>
              {results.map((location, i) => (
                <Link
                  href={location.office_link.cached_url}
                  key={location._uid}
                  onFocus={() => setIndexFocused(i)}
                >
                  {/* @ts-ignore */}
                  <Heading
                    className={classNames(styles.result, {
                      [styles.focused]: indexFocused === i,
                    })}
                    as="li"
                    size="heading-05"
                    transitionInType="none"
                    disableTriggerInOnScroll={false}
                    dangerouslySetInnerHTML={{
                      __html: location.city.replace(
                        new RegExp(searchValue, 'gi'),
                        `<span style="color:black;">${searchValue}</span>`,
                      ),
                    }}
                  />
                </Link>
              ))}
            </ul>
          ) : (
            <>
              <Text
                transitionInType="splitCharsSlideInUp"
                className={classNames(styles.quickLinksLabel)}
                as="p"
                size="label-small"
                disableTriggerInOnScroll={false}
              >
                {quick_links_label}
              </Text>

              <ul className={styles.results} ref={resultsRef}>
                {quick_links.map((link, i) => (
                  <Link href={link.full_slug} key={link.uuid} onFocus={() => setIndexFocused(i)}>
                    <Heading
                      className={classNames(styles.quickLink, {
                        [styles.focused]: indexFocused === i,
                      })}
                      as="li"
                      size="heading-05"
                      transitionInType="splitCharsSlideInUp"
                      disableTriggerInOnScroll={false}
                    >
                      {link.content.title}
                    </Heading>
                  </Link>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
